import { Switch } from '@material-ui/core';
import i18next from 'i18next';
import { isEmpty } from 'lodash-es';
import { DateTime } from 'luxon';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { IDashboardHomeCardMetricKey } from '~/src/dash/api/dashboards/home';
import {
  parseBidFloorKey,
  SmartFloorBidFloorType,
} from '~/src/dash/apiV2/smartFloors/realtimeAdaptive';
import { LoadingChart } from '~/src/dash/components/charts/LoadingChart';
import { SimpleSingleSelect } from '~/src/dash/components/dropdowns/SimpleSingleSelect';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { fontColorsDefault } from '~/src/dash/components/layout/common';
import { PillSwitcherV2 } from '~/src/dash/components/tabs/PillSwitcherV2';
import { FONT_WEIGHT } from '~/src/dash/components/text/common';
import {
  borderRadius,
  Col,
  Cols,
  Div,
  fontSizing,
  ISimpleOption,
  ISimpleOptionSelect,
  Row,
  Rows,
  spacing,
  Span,
  Text,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { convertLargeNumbersToRoundedString } from '~/src/dash/utils/basic';
import { graphColors, publicaColors } from '~/src/dash/utils/colors';
import { useClickOutside } from '~/src/dash/utils/hooks';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { useSmartFloorsLiveAdaptiveStore } from '~/src/dash/views/bidders/Bidders/SmartFloors/contexts';
import { IStackedChartData } from '~/src/dash/views/bidders/Bidders/SmartFloors/SmartFloorsLiveAdaptiveView/config';
import { SmartFloorTooltip } from '~/src/dash/views/bidders/Bidders/SmartFloors/SmartFloorsLiveAdaptiveView/Tooltip';

const controlBidFloorColor = graphColors.orange;
const smartBidFloorColor = graphColors.blueGrey;
const bidFloorColors = [
  graphColors.redOrange,
  graphColors.cyan,
  graphColors.forestGreen,
  graphColors.lightblue,
  graphColors.mustard,
  graphColors.purpleBlue,
  graphColors.coralPink,
  graphColors.seaGreen,
  graphColors.darkBlue,
  graphColors.darkPeach,
  graphColors.limeGreen,
  graphColors.lavender,
  graphColors.orange,
  graphColors.blueGrey,
  graphColors.pink,
  graphColors.turquoise,
  graphColors.purple,
  graphColors.sienna,
  graphColors.mahogany,
  graphColors.yellow,
];

const chartTypes: Array<ISimpleOption> = [
  { key: 'area', value: 'area', label: 'Area' },
  { key: 'bar', value: 'bar', label: 'Bar' },
];

interface ISmartFloorsLiveAdaptiveProps {
  bidFloorKeys: string[];
  chartData: IStackedChartData[];
  isChartAggregatedByBidFloorType: boolean;
  isChartAggregatedByTime: boolean;
  isLoading?: boolean;
  selectedMetricKey: IDashboardHomeCardMetricKey;
  updateChartData: (
    selectedMetricKey: string,
    shouldAggregateDataByTime: boolean,
    shouldAggregateDataByBidFloorType: boolean,
  ) => void;
}

export const SmartFloorsLiveAdaptive = ({
  bidFloorKeys = [],
  chartData = [],
  isChartAggregatedByBidFloorType,
  isChartAggregatedByTime,
  isLoading = true,
  selectedMetricKey,
  updateChartData,
}: ISmartFloorsLiveAdaptiveProps): React.ReactNode => {
  const { currencySymbol, session } = useBaseStore();
  const { liveConfig, selectedSite } = useSmartFloorsLiveAdaptiveStore();
  const { t } = usePublicaTranslation();

  const [chartType, setChartType] = useState('area'); // toogle between Area and Bar chart
  const [selectedBidFloorKeys, setSelectedBidFloorKeys] = useState([] as string[]); // highlighted bid floors in the chart

  // handle legend item click to select/deselect bid floors to highlight in the chart
  const handleClickLegendItem = (legendKey: string): void => {
    if (selectedBidFloorKeys.includes(legendKey)) {
      setSelectedBidFloorKeys(selectedBidFloorKeys.filter((key) => key !== legendKey));
    } else if (selectedBidFloorKeys.length === bidFloorKeys.length - 1) {
      setSelectedBidFloorKeys([]); // Deselect all if this is the last one being deselected
    } else {
      setSelectedBidFloorKeys([...selectedBidFloorKeys, legendKey]);
    }
  };

  // toggle 'Smooth' toggle button
  const handleToggleSmoothChartMode = (): void => {
    unstable_batchedUpdates(() => {
      // setIsSmoothChartMode(!isSmoothChartMode);
      updateChartData(selectedMetricKey, !isChartAggregatedByTime, isChartAggregatedByBidFloorType);
    });
  };

  // toggle 'Investigation' toggle button
  const handleToggleInvestigationChartMode = (): void => {
    unstable_batchedUpdates(() => {
      setSelectedBidFloorKeys([] as string[]);
      updateChartData(selectedMetricKey, isChartAggregatedByTime, !isChartAggregatedByBidFloorType);
    });
  };

  // format timestamp for the X-axis
  const formatTimestamp = (date: string | moment.Moment): string => {
    const luxonDate = DateTime.fromISO(moment(date).utc().toISOString()).setLocale(
      i18next?.language || 'en',
    );
    const formattedDate = luxonDate.toLocaleString({
      ...DateTime.DATE_SHORT,
      year: undefined,
      timeZone: session.publisher.Timezone,
    });
    const formattedTime = luxonDate.toLocaleString({
      ...DateTime.TIME_24_SIMPLE,
      timeZone: session.publisher.Timezone,
    });
    return date ? moment(date).utc().format(`${formattedDate} ${formattedTime}`) : '';
  };

  const getGraphColor = (bidFloorKey: string, idx: number): string => {
    if (isChartAggregatedByBidFloorType) {
      return bidFloorKey === 'Control' ? controlBidFloorColor : smartBidFloorColor;
    }
    return bidFloorColors[idx % bidFloorColors.length];
  };

  // create a ref for the legend to deselect it when clicking outside
  const containerRef = useClickOutside<HTMLDivElement>(() => {
    if (!isEmpty(selectedBidFloorKeys)) {
      setSelectedBidFloorKeys([]);
    }
  }, !!selectedBidFloorKeys);

  // reformat chart data when the selected metric key changes
  useEffect(() => {
    updateChartData(selectedMetricKey, isChartAggregatedByTime, isChartAggregatedByBidFloorType);
  }, [selectedMetricKey]);

  // reformat chart data when the selected metric key changes
  useEffect(() => {
    if (isEmpty(chartData)) {
      setSelectedBidFloorKeys([] as string[]); // reset selected bid floors when chart data is empty
      return;
    }
  }, [chartData]);

  return (
    <Div>
      <Cols style={{ alignItems: 'center' }} marginBottom={spacing.small}>
        <Col displayFlex marginRight={spacing.extraSmall}>
          <IconV2 large name='loader' color={publicaColors.darkGray} />
        </Col>
        <Col marginRight={spacing.small}>
          <Text small heavy tKey='OptimizationInsights' />
        </Col>
        <Col>
          <Text
            extraSmall
            muted
            bold
            style={{
              background: publicaColors.veryLightGray,
              borderRadius: borderRadius.base,
              padding: `0 ${spacing.extraSmall}`,
            }}
            tKey='Last12H'
          />
        </Col>
      </Cols>

      <div ref={containerRef}>
        {isEmpty(chartData) ? (
          <Text
            extraSmall
            style={{
              width: 'fit-content',
              background: publicaColors.lighterYellow,
              borderRadius: borderRadius.small,
              padding: `0 ${spacing.extraSmall}`,
            }}
            tKey='BiddersMessageSmartFloorsOptimizationInsights'
          />
        ) : (
          <Rows
            style={{
              background: publicaColors.white,
              border: `1px solid ${publicaColors.lightGray}`,
              borderRadius: borderRadius.large,
            }}
          >
            <Row largeHorizontalPadding paddingTop={spacing.small}>
              <Cols wrap style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <Col paddingTop={spacing.small}>
                  <Cols gap={spacing.base} style={{ alignItems: 'center' }}>
                    <Col>
                      <Text extraSmall inlineBlock tKey='Showing' />
                    </Col>
                    <Col>
                      {selectedSite ? (
                        <Text inlineBlock bold>
                          {selectedSite.Name} ({selectedSite.ID})
                        </Text>
                      ) : (
                        <Text inlineBlock bold tKey='NoItemSelected' tProps={{ item: t('Site') }} />
                      )}
                    </Col>
                  </Cols>
                </Col>

                <Col>
                  <Cols wrap gap={spacing.base} style={{ alignItems: 'center' }}>
                    {
                      <Col displayFlex paddingRight={spacing.extraSmall} paddingTop={spacing.small}>
                        <Text
                          small
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            paddingRight: spacing.small,
                          }}
                          tKey='Chart'
                        />
                        <PillSwitcherV2
                          size='extraSmall'
                          options={chartTypes.map((item) => ({
                            ...item,
                            isSelected: item.value === chartType,
                          }))}
                          onChange={(value) => setChartType(value)}
                        />
                      </Col>
                    }

                    <Col displayFlex paddingTop={spacing.small}>
                      <Text small style={{ display: 'flex', alignItems: 'center' }} tKey='Smooth' />
                      <Switch
                        checked={isChartAggregatedByTime}
                        name='SmoothChartMode'
                        onChange={handleToggleSmoothChartMode}
                      />
                    </Col>

                    <Col displayFlex paddingTop={spacing.small}>
                      <Text
                        small
                        style={{ display: 'flex', alignItems: 'center' }}
                        tKey='Investigation'
                      />
                      <Switch
                        checked={!isChartAggregatedByBidFloorType}
                        name='InvestigationChartMode'
                        onChange={handleToggleInvestigationChartMode}
                      />
                    </Col>

                    <Col paddingTop={spacing.small}>
                      <SimpleSingleSelect
                        inputWidth='14rem'
                        popoverWidth='14rem'
                        options={Object.values(liveConfig.metrics).map(
                          (metric): ISimpleOptionSelect => {
                            return {
                              key: metric.key,
                              label: metric.label,
                              value: metric.key,
                              isSelected: metric.key === selectedMetricKey,
                            } as ISimpleOptionSelect;
                          },
                        )}
                        placeholder={'Metric'}
                        onChange={(key: IDashboardHomeCardMetricKey): void =>
                          updateChartData(
                            key,
                            isChartAggregatedByTime,
                            isChartAggregatedByBidFloorType,
                          )
                        }
                      />
                    </Col>
                  </Cols>
                </Col>
              </Cols>
            </Row>

            {/* Chart */}
            <Row largeHorizontalPadding>
              {isLoading ? (
                <LoadingChart />
              ) : (
                <ResponsiveContainer width='100%' height={300}>
                  {chartType === 'bar' ? (
                    <BarChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray='3 3' />
                      <XAxis
                        dataKey='date'
                        tick={{
                          fontSize: fontSizing.extraSmall,
                          fill: 'black',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                        }}
                        tickFormatter={formatTimestamp}
                        domain={[0, chartData.length]}
                      />
                      <YAxis
                        type='number'
                        tick={{ fontSize: fontSizing.extraSmall, fill: 'black', textAnchor: 'end' }}
                        tickFormatter={convertLargeNumbersToRoundedString}
                        orientation='left'
                        domain={
                          selectedMetricKey === 'BidRequestPercentage' ? [0, 100] : [0, 'auto']
                        }
                        ticks={
                          selectedMetricKey === 'BidRequestPercentage' ? [0, 25, 50, 75, 100] : []
                        }
                        allowDataOverflow={false}
                      />

                      <Tooltip
                        content={
                          <SmartFloorTooltip
                            currencySymbol={currencySymbol}
                            isChartAggregatedByBidFloorType={isChartAggregatedByBidFloorType}
                            labelMetric={liveConfig.metrics[selectedMetricKey]?.label}
                            valueType={liveConfig.metrics[selectedMetricKey]?.valueType}
                            language={i18next.language || 'en'}
                            selectedBidFloorKeys={selectedBidFloorKeys}
                          />
                        }
                        cursor={{ fill: publicaColors.lightGray }}
                      />

                      {bidFloorKeys.map((bidFloorKey, idx) => {
                        return (
                          <Bar
                            key={bidFloorKey}
                            stackId='1'
                            dataKey={bidFloorKey}
                            fill={getGraphColor(bidFloorKey, idx)}
                            opacity={
                              selectedBidFloorKeys.includes(bidFloorKey) ||
                              isEmpty(selectedBidFloorKeys)
                                ? 1
                                : 0.2
                            }
                          />
                        );
                      })}
                    </BarChart>
                  ) : (
                    <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray='3 3' />
                      <XAxis
                        dataKey='date'
                        tick={{
                          fontSize: fontSizing.extraSmall,
                          fill: 'black',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                        }}
                        tickFormatter={formatTimestamp}
                        domain={[0, chartData.length]}
                      />
                      <YAxis
                        type='number'
                        tick={{ fontSize: fontSizing.extraSmall, fill: 'black', textAnchor: 'end' }}
                        tickFormatter={convertLargeNumbersToRoundedString}
                        orientation='left'
                        domain={
                          selectedMetricKey === 'BidRequestPercentage' ? [0, 100] : [0, 'auto']
                        }
                        ticks={
                          selectedMetricKey === 'BidRequestPercentage' ? [0, 25, 50, 75, 100] : []
                        }
                        allowDataOverflow={false}
                      />

                      <Tooltip
                        content={
                          <SmartFloorTooltip
                            currencySymbol={currencySymbol}
                            isChartAggregatedByBidFloorType={isChartAggregatedByBidFloorType}
                            labelMetric={liveConfig.metrics[selectedMetricKey]?.label}
                            valueType={liveConfig.metrics[selectedMetricKey]?.valueType}
                            language={i18next.language || 'en'}
                            selectedBidFloorKeys={selectedBidFloorKeys}
                          />
                        }
                        cursor={{ fill: publicaColors.lightGray }}
                      />

                      {bidFloorKeys.map((bidFloorKey, idx) => {
                        const color = getGraphColor(bidFloorKey, idx);
                        return (
                          <Area
                            stackId='1'
                            key={bidFloorKey}
                            type='monotone'
                            dataKey={bidFloorKey}
                            stroke={color}
                            fill={color}
                            opacity={
                              (!isEmpty(selectedBidFloorKeys) &&
                                selectedBidFloorKeys.includes(bidFloorKey)) ||
                              isEmpty(selectedBidFloorKeys)
                                ? 1
                                : 0
                            }
                          />
                        );
                      })}
                    </AreaChart>
                  )}
                </ResponsiveContainer>
              )}

              {/* Legend */}
              {!isLoading && bidFloorKeys.length > 0 && (
                <Cols
                  wrap
                  gap={spacing.medium}
                  style={{
                    cursor: 'pointer',
                    marginTop: spacing.medium,
                    justifyContent: 'center',
                    padding: `0 ${spacing.medium}`,
                  }}
                >
                  {bidFloorKeys.map((bidFloorKey, idx) => {
                    const { bidFloor, bidFloorType } = parseBidFloorKey(bidFloorKey);
                    const color = getGraphColor(bidFloorKey, idx);
                    return (
                      <Col
                        key={bidFloorKey}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'pointer',
                          padding: `0 ${spacing.small}`,
                          borderRadius: borderRadius.base,
                          backgroundColor: selectedBidFloorKeys.includes(bidFloorKey)
                            ? publicaColors.lightGray
                            : 'inherit',
                        }}
                        onClick={() => handleClickLegendItem(bidFloorKey)}
                      >
                        <Cols gap={spacing.extraSmall} style={{ alignItems: 'center' }}>
                          <Col paddingBottom='1px'>
                            <Text small color={color}>
                              ⬤
                            </Text>
                          </Col>
                          <Col>
                            <Text extraSmall bold>
                              {!isChartAggregatedByBidFloorType && currencySymbol}
                              {isChartAggregatedByBidFloorType ? bidFloorKey : bidFloor}
                              {!isChartAggregatedByBidFloorType &&
                                Number(bidFloorType) === Number(SmartFloorBidFloorType.Control) && (
                                  <Span
                                    style={{
                                      fontSize: fontSizing.extraSmall,
                                      color: fontColorsDefault.muted,
                                      fontWeight: FONT_WEIGHT.bold,
                                      cursor: 'pointer',
                                    }}
                                  >
                                    {' (control)'}
                                  </Span>
                                )}
                            </Text>
                          </Col>
                        </Cols>
                      </Col>
                    );
                  })}
                </Cols>
              )}
            </Row>
          </Rows>
        )}
      </div>
    </Div>
  );
};
